import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  highlightTextNodes,
  removeNodeHighlights,
} from "../common/utils/addOnServices/highlightTextNodes";
import {
  resolveSignedImageSources,
  useFailedImagePlaceholders,
} from "../common/services/microsoftImages";

// CSS for One Note rendering
const onenoteStyles = `
  #onenote-page-content {
    position: relative !important;
  }
  #onenote-page-content * {
    position: static !important;
    left: auto !important;
    top: auto !important;
    width: auto !important;
    max-width: 100% !important;
  }
  #onenote-page-content p,
  #onenote-page-content div:not([data-id]) {
    margin-left: 0 !important;
    padding-left: 0 !important;
  }
  #onenote-page-content ul,
  #onenote-page-content ol {
    margin-left: 1.5em !important;
    padding-left: 0.5em !important;
  }
  #onenote-page-content li {
    margin-left: 0 !important;
  }
  #onenote-page-content img {
    max-width: 100% !important;
    height: auto !important;
  }
  #onenote-page-content table {
    width: 100% !important;
    max-width: 100% !important;
  }
`;

const OneNotePageViewer = ({ response }: { response: any }) => {
  const [responseData, setResponseData] = useState<any>(null);
  const fileViewerData = useSelector((state: any) => state.fileViewerData);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (response) {
      response.json().then((data: any) => {
        setResponseData(data);
      });
    }
  }, [response]);

  // The page shows at once: each image loads by itself from the signed URL
  // the preview wrote, and one that fails becomes a placeholder.
  const content = useMemo(() => {
    if (!responseData?.content) return "";
    const doc = new DOMParser().parseFromString(
      responseData.content,
      "text/html"
    );
    return resolveSignedImageSources(doc.body.innerHTML);
  }, [responseData]);
  useFailedImagePlaceholders(contentRef, content);

  useEffect(() => {
    if (content) {
      highlightTextNodes("#onenote-page-content", fileViewerData);
    }
  }, [fileViewerData, content]);

  return (
    <div className="h-full w-full p-6">
      <style>{onenoteStyles}</style>
      <div className="w-full h-[100px]">
        <div className="w-[300px] mt-[20px] text-[24px] border-b pb-1 overflow-hidden text-ellipsis whitespace-nowrap">
          {responseData?.title || "Untitled Page"}
        </div>
        <div className="w-[400px] text-[12px] mt-1 text-gray-400 flex items-center gap-1">
          {responseData?.last_modified &&
            new Date(responseData?.last_modified).toLocaleDateString("en-US", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
        </div>
      </div>
      <div
        ref={contentRef}
        dangerouslySetInnerHTML={{ __html: content }}
        id="onenote-page-content"
        className="relative"
        onClick={(e) => {
          // Check if the clicked element is a link or contained within a link
          const target = e.target as HTMLElement;
          const link = target.closest("a");

          if (link && link.href) {
            e.preventDefault();
            e.stopPropagation();
            window.open(link.href, "_blank", "noopener,noreferrer");
            return;
          }

          removeNodeHighlights("#onenote-page-content");
        }}
      ></div>
    </div>
  );
};

export default OneNotePageViewer;
